/**
 * Collider-proxy → Rapier descriptor (Graphics G1).
 *
 * The cooker stores a SIMPLIFIED physics proxy per asset (a trimesh for an enterable
 * structure, a convex hull for a solid prop) as JSON in the store. This builds the
 * matching Rapier collider descriptor from that JSON. Pure: it takes the RAPIER
 * runtime as a parameter (type-only import) so it bundles into a Node harness.
 */
import type RAPIER_NS from '@dimforge/rapier3d-simd-compat'; // engine-identity types (see PhysicsModule)

export interface ColliderProxy {
  shape: 'trimesh_static' | 'convex_hull' | 'box';
  positions?: number[];
  indices?: number[];
  points?: number[];
  halfExtents?: [number, number, number];
}

/** Build a Rapier collider descriptor from a stored proxy JSON (null if malformed). */
export function colliderDescFromProxy(RAPIER: typeof RAPIER_NS, proxy: ColliderProxy): RAPIER_NS.ColliderDesc | null {
  switch (proxy.shape) {
    case 'trimesh_static': {
      if (!proxy.positions?.length || !proxy.indices?.length) return null;
      return RAPIER.ColliderDesc.trimesh(new Float32Array(proxy.positions), new Uint32Array(proxy.indices));
    }
    case 'convex_hull': {
      if (!proxy.points?.length) return null;
      return RAPIER.ColliderDesc.convexHull(new Float32Array(proxy.points)); // Rapier computes the hull
    }
    case 'box': {
      const h = proxy.halfExtents ?? [0.5, 0.5, 0.5];
      return RAPIER.ColliderDesc.cuboid(h[0], h[1], h[2]);
    }
    default:
      return null;
  }
}
